import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/** Lebenszyklus eines Projekts. `merged` = zusammengeführt (#123): nur lesbar, überall ausgeblendet. */
export const LIFECYCLE_STATUS = ['active', 'completed', 'merged'] as const;
export type LifecycleStatus = (typeof LIFECYCLE_STATUS)[number];

/**
 * Gründe, warum ein Auftrag nicht aus seinem Quellprojekt verschoben werden darf (#112).
 * Eine Quelle für Service (409) und Auswahlliste (ausgegraut). `quelle_abgeschlossen` ist die
 * ältere Regel, jetzt nur auch sichtbar. `sf_uebertragung` = läuft noch (warten hilft),
 * `sf_uebertragung_fehlgeschlagen` = dauerhaft gescheitert (erledigt sich nie selbst, im Projekt
 * verwerfen). `sperrdaten_fehlen` = Sperrprüfung nicht möglich → fail-closed gesperrt.
 * `herkunft_konflikt` (#123, architektur.md §15 Nr. 27) hängt am AUFTRAG, nicht an der Quelle: Er
 * ist der letzte seines Projekts und selbst führender Auftrag eines schon zusammengeführten
 * Projekts — die Quelle würde das zweite Ur-Projekt desselben Auftrags.
 */
export const AUFTRAG_SPERRGRUENDE = [
  'quelle_abgeschlossen',
  'phase_vor_setup',
  'setup_phase_unbekannt',
  'kfml_worklist',
  'faelligkeit',
  'sf_uebertragung',
  'sf_uebertragung_fehlgeschlagen',
  'herkunft_konflikt',
  'sperrdaten_fehlen',
] as const;
export const AuftragSperrgrundSchema = z.enum(AUFTRAG_SPERRGRUENDE);
export type AuftragSperrgrund = z.infer<typeof AuftragSperrgrundSchema>;

/**
 * Herkunftsmarke an umgehängten Einträgen (#123). Per `.extend(HerkunftFelder)` an die
 * Zeilen-Schemas hängen — NICHT per Intersection (hat schon die MCP-Toolliste gekillt).
 * Ohne diese Felder im Schema entfernt Zod sie still aus jeder Antwort.
 */
export const HerkunftFelder = {
  herkunft_project_id: UuidSchema.nullable().optional(),
  herkunft_project_key: z.string().nullable().optional(),
};

const ProjektVerweisSchema = z.object({ project_id: UuidSchema, project_key: z.string() });

/** Banner/Hinweis im Projektdetail (architektur.md §9). */
export const ZusammenfuehrungSchema = z.object({
  /**
   * `project_key` darf fehlen (Review V4): Lässt sich die P-Nummer des Ziels nicht auflösen, bleibt
   * der Banner trotzdem stehen — ein zusammengeführtes Projekt ohne Hinweis sähe aus wie ein
   * gesperrtes ohne Grund.
   */
  in: ProjektVerweisSchema.extend({ project_key: z.string().nullable() }).nullable(),
  am: IsoDateTimeSchema.nullable(),
  von_name: z.string().nullable(),
  aktuell_in: ProjektVerweisSchema.nullable(),
  aus: z.array(ProjektVerweisSchema.extend({ am: IsoDateTimeSchema })),
});
export type Zusammenfuehrung = z.infer<typeof ZusammenfuehrungSchema>;
